import { hydrateRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import App from './App.tsx'
import './index.css'
import i18n from './lib/i18n'
import { monitorSEOPerformance, trackSEOEvents } from './lib/seoAnalytics'
import { initializeEnhancedAnalytics } from './lib/enhancedAnalytics'
import { preloadCriticalResources } from './hooks/usePerformanceOptimization'
import type { SSRState } from './shared/routes'

// Get initial state from server (hydration from SSR)
declare global {
  interface Window {
    __INITIAL_STATE__?: SSRState
  }
}

const initialState = window.__INITIAL_STATE__

// Initialize i18next with the language from server state (don't recalculate on client)
if (initialState?.lang) {
  i18n.changeLanguage(initialState.lang)
}

// Initialize performance monitoring and optimizations (client-side only)
if (typeof window !== 'undefined') {
  monitorSEOPerformance();
  initializeEnhancedAnalytics();
  preloadCriticalResources();

  // Ensure any previously installed Service Workers are unregistered and caches cleared (temporary)
  if ('serviceWorker' in navigator && import.meta.env.PROD) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(reg => reg.unregister());
      }).catch(() => {});
      if ('caches' in window) {
        caches.keys().then(keys => {
          keys.forEach(key => caches.delete(key));
        }).catch(() => {});
      }
    });
  }

  // Wire Core Web Vitals reporting (deferred to keep main bundle lean)
  import('web-vitals')
    .then(({ onCLS, onFCP, onINP, onLCP, onTTFB }) => {
      const report = (metric: { name: string; value: number }) => {
        try {
          trackSEOEvents.webVitals(metric.name, metric.value);
        } catch {}
      };

      onCLS(report);
      onFCP(report);
      onINP(report);
      onLCP(report);
      onTTFB(report);
    })
    .catch(() => {});
}

// Hydrate the app with SSR-rendered HTML (BrowserRouter, no manual navigation)
hydrateRoot(
  document.getElementById("root")!,
  <HelmetProvider>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </HelmetProvider>
);
